import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Check, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, compressImageFile, shrinkGallery } from "../../lib/api";
import { SortableGallery } from "../../components/gallery/SortableGallery";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STEPS = ["Basis", "Kenmerken", "Presentatie", "Preview"];
const ENERGY = ["A", "B", "C", "D", "E", "F", "G", "—"];

const initial = {
  title: "", street: "", city: "", neighborhood: "", postcode: "",
  price: 0, bedrooms: 1, m2: 0, energy_label: "—",
  available_from: "Direct", furnished: false, pets: false, balcony: false, parking: false,
  description: "", image: null, gallery: [], income_required: null, lat: null, lng: null,
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-semibold text-navy">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const HuurWizard = () => {
  const { lid } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [f, setF] = useState(initial);
  const [id, setId] = useState(lid || null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!lid);
  const dirty = useRef(false);
  const set = (k, v) => { dirty.current = true; setF((p) => ({ ...p, [k]: v })); };

  useEffect(() => {
    if (!lid) return;
    api("/makelaar/listings")
      .then((d) => {
        const found = (d.listings || []).find((l) => l.id === lid);
        if (found) setF({ ...initial, ...found, gallery: found.gallery || [] });
      })
      .catch(() => toast.error("Kon woning niet laden"))
      .finally(() => setLoading(false));
  }, [lid]);

  const payload = () => ({
    ...f,
    price: Number(f.price) || 0,
    bedrooms: Number(f.bedrooms) || 1,
    m2: Number(f.m2) || 0,
    income_required: numOrNull(f.income_required),
    lat: numOrNull(f.lat),
    lng: numOrNull(f.lng),
    image: f.image || (f.gallery && f.gallery[0]) || null,
  });

  const save = async (silent = false) => {
    if (!f.street.trim() || !f.city.trim()) {
      if (!silent) toast.error("Vul minimaal straat en plaats in om op te slaan.");
      return null;
    }
    if (!f.title.trim()) {
      if (!silent) toast.error("Geef je advertentie een titel.");
      return null;
    }
    setSaving(true);
    try {
      const safeGallery = await shrinkGallery(f.gallery || []);
      const body = { ...payload(), gallery: safeGallery, image: safeGallery[0] || null };
      let res;
      if (id) res = await api(`/makelaar/listings/${id}`, { method: "PATCH", body: JSON.stringify(body) });
      else { res = await api("/makelaar/listings", { method: "POST", body: JSON.stringify(body) }); setId(res.id); }
      setF((p) => ({ ...p, gallery: safeGallery, image: safeGallery[0] || null }));
      dirty.current = false;
      if (!silent) toast.success("Opgeslagen");
      return res;
    } catch (e) { toast.error(e.message); return null; } finally { setSaving(false); }
  };

  const validate = () => {
    if (step === 0) {
      if (!f.title.trim()) return "Vul een titel in.";
      if (!f.street.trim() || !f.city.trim()) return "Straat en plaats zijn verplicht.";
      if (!f.price || Number(f.price) <= 0) return "Vul een huurprijs in.";
    }
    return null;
  };

  const next = async () => {
    const err = validate();
    if (err) return toast.error(err);
    if (dirty.current) await save(true);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const uploadPhotos = async (files) => {
    const current = f.gallery || [];
    const room = Math.max(0, 40 - current.length);
    if (room === 0) { toast.error("Maximaal 40 foto's per advertentie."); return; }
    const arr = Array.from(files).slice(0, room);
    try {
      const encoded = await Promise.all(arr.map((file) => compressImageFile(file)));
      set("gallery", [...current, ...encoded]);
    } catch { toast.error("Uploaden mislukt"); }
  };
  const removePhoto = (i) => set("gallery", f.gallery.filter((_, idx) => idx !== i));

  const publish = async () => {
    const res = await save(true);
    if (!res && !id) return;
    toast.success("Huurwoning gepubliceerd!");
    navigate("/makelaar");
  };

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-brandblue" /></div>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold text-navy" data-testid="huur-wizard-title">
          {id ? "Huurwoning bewerken" : "Nieuwe huurwoning"}
        </h1>
        <Button variant="outline" onClick={() => save(false)} disabled={saving} className="rounded-xl" data-testid="save-draft">
          {saving ? <Loader2 className="animate-spin" size={16} /> : "Opslaan"}
        </Button>
      </div>

      <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <button
            key={s}
            onClick={() => i <= step && setStep(i)}
            className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${
              i === step ? "bg-brandblue text-white" : i < step ? "bg-brandblue-light text-brandblue" : "bg-slate-100 text-slate-400"
            }`}
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
              {i < step ? <Check size={12} /> : i + 1}
            </span>{s}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4 rounded-3xl border border-border bg-white p-6 shadow-sm">
        {step === 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Titel"><Input data-testid="wz-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Licht 3-kamerappartement in centrum" className="rounded-xl" /></Field>
            <Field label="Huurprijs per maand (€)"><Input data-testid="wz-price" type="number" value={f.price || ""} onChange={(e) => set("price", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Straat"><Input data-testid="wz-street" value={f.street} onChange={(e) => set("street", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Plaats"><Input data-testid="wz-city" value={f.city} onChange={(e) => set("city", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Wijk / buurt"><Input value={f.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Postcode"><Input value={f.postcode} onChange={(e) => set("postcode", e.target.value)} className="rounded-xl" /></Field>
          </div>
        )}

        {step === 1 && (
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Slaapkamers"><Input type="number" value={f.bedrooms || ""} onChange={(e) => set("bedrooms", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Woonopp. (m²)"><Input type="number" value={f.m2 || ""} onChange={(e) => set("m2", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Energielabel">
              <Select value={f.energy_label} onValueChange={(v) => set("energy_label", v)}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{ENERGY.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Beschikbaar vanaf"><Input value={f.available_from} onChange={(e) => set("available_from", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Inkomens-eis (€/mnd)"><Input type="number" value={f.income_required ?? ""} onChange={(e) => set("income_required", e.target.value)} className="rounded-xl" /></Field>
            <div className="sm:col-span-3 grid gap-3 sm:grid-cols-4">
              {[
                ["furnished", "Gemeubileerd"],
                ["pets", "Huisdieren toegestaan"],
                ["balcony", "Balkon"],
                ["parking", "Parkeerplek"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 rounded-xl border border-border bg-white p-3 text-sm font-medium text-navy">
                  <Switch checked={!!f[k]} onCheckedChange={(v) => set(k, v)} /> {label}
                </label>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <Field label="Beschrijving"><Textarea rows={6} value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl" placeholder="Vertel kort over de woning, buurt en voorzieningen…" /></Field>
            <div>
              <Label className="text-sm font-semibold text-navy">Foto&apos;s ({f.gallery?.length || 0} / 40)</Label>
              <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-white py-6 text-sm font-semibold text-brandblue hover:border-brandblue">
                <Upload size={16} /> Foto&apos;s uploaden
                <input data-testid="wz-upload" type="file" accept="image/*" multiple onChange={(e) => uploadPhotos(e.target.files)} className="hidden" />
              </label>
              <div className="mt-4">
                <SortableGallery
                  value={f.gallery || []}
                  onChange={(next) => { set("gallery", next); set("image", next[0] || null); }}
                  onRemove={removePhoto}
                  data-testid="huur-gallery"
                />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="font-display text-lg font-bold text-navy">Preview</h2>
            {f.gallery?.[0] && <img src={f.gallery[0]} alt="" className="h-56 w-full rounded-2xl object-cover" />}
            <div>
              <div className="font-display text-xl font-bold text-navy">{f.title || "—"}</div>
              <div className="text-sm text-slate-500">{f.street}, {f.city}</div>
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-slate-600">
              <span>€{Number(f.price || 0).toLocaleString("nl-NL")}/mnd</span>
              <span>· {f.bedrooms} slaapkamers</span>
              <span>· {f.m2} m²</span>
              <span>· Energielabel {f.energy_label}</span>
            </div>
            {f.description && <p className="whitespace-pre-wrap text-sm text-slate-700">{f.description}</p>}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="outline" onClick={prev} disabled={step === 0} className="rounded-xl">Vorige</Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next} className="rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="wz-next">Volgende</Button>
        ) : (
          <Button onClick={publish} className="rounded-xl bg-success hover:bg-success/90" data-testid="wz-publish">Publiceren</Button>
        )}
      </div>
    </div>
  );
};

export default HuurWizard;
